import React, {useContext} from 'react';
import {
  IntegrationResponse,
  isCrmIntegrationResponse,
  LinkedInIntegrationResponse,
  SlackIntegrationResponse,
} from '../../shared/api/definitions/integration';
import {IntegrationType} from '../../shared/integrations/common';
import {AllIntegrations} from './AllIntegrations';
import {WebIntentIntegration} from '../../shared/integrations/webIntent';

const IntegrationContext = React.createContext<IntegrationResponse | null>(
  null
);

export const IntegrationProvider = ({
  children,
  integrationResponse,
}: {
  children: React.ReactNode;
  integrationResponse: IntegrationResponse;
}) => {
  return (
    <IntegrationContext.Provider value={integrationResponse}>
      {children}
    </IntegrationContext.Provider>
  );
};

export const useCrmIntegrationFromContext = () => {
  const integration = useIntegrationFromContext();
  if (!isCrmIntegrationResponse(integration)) {
    throw new Error('Integration is not a CRM integration');
  }
  return integration;
};

export function useIntegrationFromContext(): IntegrationResponse;
export function useIntegrationFromContext(
  type: 'linkedin'
): LinkedInIntegrationResponse;
export function useIntegrationFromContext(
  type: 'slack'
): SlackIntegrationResponse;
export function useIntegrationFromContext(
  type: 'webIntent'
): WebIntentIntegration;
export function useIntegrationFromContext(
  type?: IntegrationType
):
  | IntegrationResponse
  | LinkedInIntegrationResponse
  | SlackIntegrationResponse
  | WebIntentIntegration {
  const integration = useContext(IntegrationContext);
  if (!integration) {
    throw new Error('Integration details not found in context');
  }

  // If no type parameter provided, return the union type
  if (!type) {
    return integration;
  }

  // Simple type check based on integration.type
  if (integration.type !== type) {
    throw new Error(
      `Integration type mismatch: expected ${type}, got ${integration.type}`
    );
  }

  return integration;
}

export const useIntegrationStaticInfo = () => {
  const integration = useIntegrationFromContext();
  return AllIntegrations[integration.type];
};
